import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import { brand } from '../data/mock';
import { MapPin, ArrowRight, Users, TrendingUp, Award, Heart, Upload, CheckCircle2, AlertCircle, Mail, FileText, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RESUME_BYTES = 5 * 1024 * 1024; // 5 MB
const ALLOWED_RESUME_EXT = ['pdf', 'doc', 'docx'];

export default function Career() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', role: '', message: '' });
  const [resume, setResume] = useState(null);
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const fileInputRef = useRef(null);

  const onFileChange = (e) => {
    setErrorMsg('');
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_RESUME_EXT.includes(ext)) {
      setStatus('error');
      setErrorMsg('Resume must be a PDF or Word document (.pdf, .doc, .docx).');
      e.target.value = '';
      return;
    }
    if (file.size > MAX_RESUME_BYTES) {
      setStatus('error');
      setErrorMsg('Resume must be smaller than 5 MB.');
      e.target.value = '';
      return;
    }
    setResume(file);
    setStatus('idle');
  };

  const clearResume = () => {
    setResume(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onPhoneChange = (e) => {
    // Strip everything except digits, cap at 10
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm({ ...form, phone: digits });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Client-side validation: phone must be exactly 10 digits
    if (!/^\d{10}$/.test(form.phone)) {
      setStatus('error');
      setErrorMsg('Please enter a valid 10-digit mobile number.');
      return;
    }
    setStatus('submitting');
    setErrorMsg('');
    try {
      const data = new FormData();
      data.append('name', form.name);
      data.append('email', form.email);
      data.append('phone', form.phone);
      data.append('role', form.role);
      if (form.message) data.append('message', form.message);
      if (resume) data.append('resume', resume);
      await axios.post(`${API}/applications`, data);
      setStatus('success');
      setForm({ name: '', email: '', phone: '', role: '', message: '' });
      clearResume();
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      setStatus('error');
      const d = err?.response?.data?.detail;
      let msg = 'Something went wrong. Please try again.';
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length) msg = d.map((e) => e?.msg || 'Invalid input').join(' · ');
      else if (d && typeof d === 'object' && d.msg) msg = String(d.msg);
      setErrorMsg(msg);
    }
  };

  return (
    <div>
      <PageHero
        title="Build a career that keeps India running."
        subtitle="Join a growing team of trained professionals delivering trusted facility services across corporate campuses, hospitals, malls, universities and factories."
        image="/assets/career-hero.webp"
        breadcrumbs={[{ label: 'Career' }]}
      />

      {/* Why work here */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Why Durga</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-[1.1] tracking-tight">A workplace that invests in your growth.</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { Icon: TrendingUp, title: 'Structured growth', desc: 'Clear career paths from executive to leadership across every function.' },
              { Icon: Award, title: 'Recognition', desc: 'Monthly and yearly awards for standout performance and initiative.' },
              { Icon: Users, title: 'Learning culture', desc: 'Ongoing training on equipment, safety, soft skills and compliance.' },
              { Icon: Heart, title: 'Care & compliance', desc: 'Statutory-compliant payroll, ESI, PF and welfare benefits.' },
            ].map(({ Icon, title, desc }) => (
              <div key={title} className="bg-slate-50 p-6 rounded-2xl border border-slate-100 hover:shadow-lg hover:-translate-y-0.5 transition-all">
                <div className="w-11 h-11 rounded-lg bg-[#0A2540] text-white flex items-center justify-center mb-4">
                  <Icon size={20} />
                </div>
                <h3 className="font-bold text-slate-900 mb-1.5">{title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Apply Form */}
      <section id="apply" className="py-24 bg-white">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div>
            <div className="text-[#0A2540] uppercase text-xs font-bold tracking-[0.25em] mb-4">Apply Now</div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-[1.1] tracking-tight mb-5">Send us your details.</h2>
            <p className="text-slate-600 leading-relaxed">
              Share your background and role of interest. Our HR team reviews every application and gets back within 5–10 business days.
            </p>

            <ul className="mt-8 space-y-3">
              {['Confidential process', 'Structured interviews', 'Fair pay & benefits'].map((p) => (
                <li key={p} className="flex items-center gap-3 text-sm text-slate-700"><CheckCircle2 size={16} className="text-[#F5B800]" /> {p}</li>
              ))}
            </ul>

            <div className="mt-8 p-4 rounded-xl bg-slate-50 border border-slate-100 flex items-start gap-3">
              <Mail size={18} className="text-[#0A2540] shrink-0 mt-0.5" />
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">Send resume by email</div>
                <a href={`mailto:${brand.hrEmail}`} className="text-[#0A2540] font-semibold text-sm hover:underline">{brand.hrEmail}</a>
                <div className="text-xs text-slate-500 mt-1">Attach your CV — we&rsquo;ll get back within 5&ndash;10 business days.</div>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} className="bg-slate-50 rounded-2xl p-8 border border-slate-100 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Full Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Phone</label>
                <input required type="tel" inputMode="numeric" maxLength={10} placeholder="10-digit mobile" title="Enter your 10-digit mobile number" value={form.phone} onChange={onPhoneChange} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" data-testid="career-phone-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Role Applying For</label>
              <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all">
                <option value="">Select an area of interest</option>
                <option>Facility Management / Operations</option>
                <option>Housekeeping Supervisor</option>
                <option>Security Officer</option>
                <option>Technical / MEP Maintenance</option>
                <option>Pest Control Technician</option>
                <option>Front Office / Reception</option>
                <option>Food & Beverage</option>
                <option>Horticulture</option>
                <option>Drone Pilot — Facade Cleaning</option>
                <option>Sales / Business Development</option>
                <option>HR / Payroll / Compliance</option>
                <option>Other / Open Application</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Cover Note</label>
              <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all" placeholder="A few lines about your experience and availability..." />
            </div>
            <div>
              {!resume ? (
                <label
                  className="flex items-center gap-3 px-4 py-3 bg-white border-2 border-dashed border-slate-200 rounded-lg cursor-pointer hover:border-[#F5B800] transition-colors text-sm text-slate-600"
                  data-testid="career-resume-label"
                >
                  <Upload size={16} /> Upload resume (PDF, DOC, DOCX · max 5 MB)
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={onFileChange}
                    data-testid="career-resume-input"
                  />
                </label>
              ) : (
                <div
                  className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-lg text-sm text-slate-700"
                  data-testid="career-resume-chip"
                >
                  <FileText size={16} className="text-[#0A2540] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">{resume.name}</div>
                    <div className="text-xs text-slate-500">
                      {(resume.size / 1024).toFixed(0)} KB · attached
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearResume}
                    className="text-slate-400 hover:text-red-500 transition-colors"
                    aria-label="Remove attached resume"
                    data-testid="career-resume-remove"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}
            </div>
            <button type="submit" disabled={status === 'submitting'} className="w-full btn-cta font-semibold py-3 rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed">
              {status === 'submitting' ? 'Sending…' : status === 'success' ? 'Application received' : 'Submit Application'}
            </button>
            {status === 'success' && (
              <div className="text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-4 py-3 flex items-center gap-2">
                <CheckCircle2 size={16} /> Thanks! Our HR team will be in touch soon.
              </div>
            )}
            {status === 'error' && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3 flex items-center gap-2">
                <AlertCircle size={16} /> {errorMsg}
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
